import React, { useState } from "react";
import SimulationDataGeneratorAPI from "../services/SimulationDataGeneratorAPI";
import LoadingSpinner from "../common/LoadingSpinner";

/**
 * Compliance Scanner tab — scan production messages for trademark/trade dress
 * violations and missing simulation disclaimers. Preview findings then apply fixes.
 *
 * Workflow:
 * 1. Admin clicks "Run Audit" → dry-run scan returns all violation details
 * 2. Admin reviews per-message infraction list
 * 3. Admin clicks "Apply All Fixes" → backend saves corrected messages
 */
const ComplianceScanner = () => {
  const [auditResult, setAuditResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [fixResult, setFixResult] = useState(null);

  // Format channel for display: "SOCIAL_MEDIA" → "Social Media", "EMAIL" → "Email"
  const formatChannel = (ch) => {
    if (!ch) return ch;
    return ch
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleChannelFilterChange = (value) => {
    setChannelFilter(value);
    // Clear stale results so user knows to re-run the audit
    setAuditResult(null);
    setFixResult(null);
    setSuccessMsg("");
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 5000);
  };

  // ── Run dry-run audit ──
  const handleAudit = async () => {
    setLoading(true);
    setError(null);
    setAuditResult(null);
    setFixResult(null);
    setSuccessMsg("");
    try {
      const result = await SimulationDataGeneratorAPI.auditCompliance(
        channelFilter || undefined,
      );
      setAuditResult(result);
      if (result.totalScanned === 0 && channelFilter) {
        // Filter returned 0 — help user diagnose whether the filter or data is the issue
        try {
          const stats = await SimulationDataGeneratorAPI.getStats();
          const channelLabel = formatChannel(channelFilter);
          const totalProd = stats?.totalMessages || 0;

          // Log detailed channel breakdown for diagnostics
          console.log("[ComplianceScanner] Stats channel breakdown:", {
            totalMessages: stats?.totalMessages,
            emailCount: stats?.emailCount,
            smsCount: stats?.smsCount,
            voiceCount: stats?.voiceCount,
            socialMediaCount: stats?.socialMediaCount,
            selectedFilter: channelFilter,
          });

          // Look up the specific count for the selected channel
          const channelCountMap = {
            EMAIL: stats?.emailCount,
            SMS: stats?.smsCount,
            VOICE: stats?.voiceCount,
            SOCIAL_MEDIA: stats?.socialMediaCount,
          };
          const selectedChannelCount = channelCountMap[channelFilter] || 0;

          if (totalProd === 0) {
            setError(
              `No production messages found in your message library. ` +
                `The Compliance Scanner only audits approved production messages. ` +
                `Generate and approve messages first via the Approval Dashboard.`,
            );
          } else if (selectedChannelCount === 0) {
            setError(
              `No ${channelLabel} production messages found (${totalProd} total: ` +
                `${stats?.emailCount || 0} Email, ${stats?.smsCount || 0} SMS, ` +
                `${stats?.voiceCount || 0} Voice, ${stats?.socialMediaCount || 0} Social Media). ` +
                `Try "All Channels" or check the Message Library to verify channel types.`,
            );
          } else {
            // Stats says there ARE messages for this channel, but audit returned 0.
            // This indicates a backend code mismatch — likely needs redeployment.
            setError(
              `Stats reports ${selectedChannelCount} ${channelLabel} production messages, ` +
                `but the compliance audit found 0. This may indicate the backend needs ` +
                `to be redeployed. Check server logs for channel distribution details.`,
            );
          }
        } catch {
          // If stats call fails, just show a generic empty message
          showSuccess(
            `No ${formatChannel(channelFilter)} production messages found to scan.`,
          );
        }
      } else if (result.totalScanned === 0 && !channelFilter) {
        setError(
          `No production messages found. The Compliance Scanner only audits ` +
            `approved production messages. Generate and approve messages first ` +
            `via the Generate and Approval Dashboard tabs.`,
        );
      } else if (result.issuesFound === 0) {
        showSuccess(
          `All ${result.totalScanned} production messages are already compliant.`,
        );
      }
    } catch (err) {
      console.error("Compliance audit failed:", err);
      setError("Compliance audit failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Apply fixes ──
  const handleApplyFixes = async () => {
    if (
      !window.confirm(
        `This will modify ${auditResult?.issuesFound || 0} production message(s) to resolve compliance violations.\n\nThis action cannot be undone. Continue?`,
      )
    ) {
      return;
    }

    setApplying(true);
    setError(null);
    try {
      const result = await SimulationDataGeneratorAPI.applyComplianceFix(
        channelFilter || undefined,
      );
      setFixResult(result);
      setAuditResult(null);
      showSuccess(
        `Compliance fixes applied: ${result.messagesFixed} message(s) updated, ${result.alreadyCompliant} already compliant.`,
      );
    } catch (err) {
      console.error("Compliance fix failed:", err);
      setError("Failed to apply compliance fixes. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  const toggleExpand = (uuid) => {
    setExpandedId(expandedId === uuid ? null : uuid);
  };

  // Pick badge color based on severity
  const severityClass = (infractions) => {
    if (!infractions || infractions.length === 0) return "compliance-badge-ok";
    if (infractions.length >= 3) return "compliance-badge-critical";
    return "compliance-badge-warning";
  };

  const displayResult = fixResult || auditResult;
  const isFixResult = !!fixResult;

  return (
    <div className="compliance-scanner">
      {/* ── Controls ── */}
      <div className="compliance-controls">
        <div className="compliance-controls-row">
          <div
            className="form-group"
            style={{ minWidth: 180, flex: "0 1 auto" }}
          >
            <label htmlFor="compliance-channel-filter">Channel Filter</label>
            <select
              id="compliance-channel-filter"
              value={channelFilter}
              onChange={(e) => handleChannelFilterChange(e.target.value)}
              className="form-select"
            >
              <option value="">All Channels</option>
              <option value="EMAIL">Email</option>
              <option value="SMS">SMS</option>
              <option value="VOICE">Voice</option>
              <option value="SOCIAL_MEDIA">Social Media</option>
            </select>
          </div>

          <div className="compliance-actions">
            <button
              className="btn-compliance-audit"
              onClick={handleAudit}
              disabled={loading || applying}
            >
              {loading ? "Scanning..." : "Run Audit"}
            </button>

            {auditResult && auditResult.issuesFound > 0 && (
              <button
                className="btn-compliance-fix"
                onClick={handleApplyFixes}
                disabled={applying}
              >
                {applying
                  ? "Applying..."
                  : `Apply All Fixes (${auditResult.issuesFound})`}
              </button>
            )}
          </div>
        </div>

        <p className="compliance-description">
          Scans all production messages for trademark/trade dress violations
          (proprietary logos, brand colors, ®/™ symbols) and missing simulation
          disclaimers. Run the audit first to preview findings, then apply
          fixes.
        </p>
      </div>

      {/* ── Loading / Error / Success ── */}
      {loading && (
        <div className="compliance-loading">
          <LoadingSpinner />
          <p>Scanning production messages for compliance issues...</p>
        </div>
      )}

      {error && <div className="compliance-error">{error}</div>}
      {successMsg && <div className="compliance-success">{successMsg}</div>}

      {/* ── Summary Card ── */}
      {displayResult && !loading && (
        <div className="compliance-summary">
          <h3>{isFixResult ? "Fix Results" : "Audit Results"}</h3>
          <div className="compliance-summary-grid">
            <div className="compliance-stat">
              <span className="compliance-stat-value">
                {displayResult.totalScanned}
              </span>
              <span className="compliance-stat-label">Scanned</span>
            </div>
            <div className="compliance-stat">
              <span className="compliance-stat-value compliance-stat-issues">
                {displayResult.issuesFound}
              </span>
              <span className="compliance-stat-label">Issues Found</span>
            </div>
            <div className="compliance-stat">
              <span className="compliance-stat-value compliance-stat-compliant">
                {displayResult.alreadyCompliant}
              </span>
              <span className="compliance-stat-label">Already Compliant</span>
            </div>
            {isFixResult && (
              <div className="compliance-stat">
                <span className="compliance-stat-value compliance-stat-fixed">
                  {displayResult.messagesFixed}
                </span>
                <span className="compliance-stat-label">Fixed</span>
              </div>
            )}
          </div>

          {/* Infraction summary */}
          {displayResult.infractionSummary &&
            displayResult.infractionSummary.length > 0 && (
              <div className="compliance-infraction-summary">
                <h4>Infraction Types</h4>
                <ul>
                  {displayResult.infractionSummary.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      )}

      {/* ── Per-Message Details ── */}
      {displayResult &&
        displayResult.auditResults &&
        displayResult.auditResults.length > 0 && (
          <div className="compliance-results">
            <h3>
              {isFixResult ? "Fixed Messages" : "Messages with Issues"} (
              {displayResult.auditResults.length})
            </h3>

            {displayResult.auditResults.map((audit) => (
              <div
                key={audit.uuid}
                className={`compliance-message-card ${expandedId === audit.uuid ? "expanded" : ""}`}
              >
                <div
                  className="compliance-message-header"
                  onClick={() => toggleExpand(audit.uuid)}
                >
                  <div className="compliance-message-title">
                    <span
                      className={`compliance-badge ${severityClass(audit.infractions)}`}
                    >
                      {audit.infractions?.length || 0} issue
                      {(audit.infractions?.length || 0) !== 1 ? "s" : ""}
                    </span>
                    <strong>{audit.subject || "(No Subject)"}</strong>
                    <span className="compliance-channel-tag">
                      {formatChannel(audit.communicationChannel)}
                    </span>
                  </div>
                  <span className="compliance-expand-icon">
                    {expandedId === audit.uuid ? "▾" : "▸"}
                  </span>
                </div>

                {expandedId === audit.uuid && (
                  <div className="compliance-message-details">
                    <div className="compliance-detail-row">
                      <span className="compliance-detail-label">UUID:</span>
                      <code>{audit.uuid}</code>
                    </div>
                    <div className="compliance-detail-row">
                      <span className="compliance-detail-label">
                        Message ID:
                      </span>
                      <code>{audit.messageId}</code>
                    </div>

                    {/* Infractions */}
                    {audit.infractions && audit.infractions.length > 0 && (
                      <div className="compliance-section">
                        <h4 className="compliance-section-title compliance-section-infractions">
                          Infractions
                        </h4>
                        <ul className="compliance-list compliance-list-infractions">
                          {audit.infractions.map((infraction, i) => (
                            <li key={i}>{infraction}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Fixes Applied */}
                    {audit.fixesApplied && audit.fixesApplied.length > 0 && (
                      <div className="compliance-section">
                        <h4 className="compliance-section-title compliance-section-fixes">
                          {isFixResult ? "Fixes Applied" : "Fixes to Apply"}
                        </h4>
                        <ul className="compliance-list compliance-list-fixes">
                          {audit.fixesApplied.map((fix, i) => (
                            <li key={i}>{fix}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Remaining Warnings */}
                    {audit.remainingWarnings &&
                      audit.remainingWarnings.length > 0 && (
                        <div className="compliance-section">
                          <h4 className="compliance-section-title compliance-section-warnings">
                            Remaining Warnings
                          </h4>
                          <ul className="compliance-list compliance-list-warnings">
                            {audit.remainingWarnings.map((warning, i) => (
                              <li key={i}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                    {/* Referenced Brands */}
                    {audit.referencedBrands &&
                      audit.referencedBrands.length > 0 && (
                        <div className="compliance-section">
                          <h4 className="compliance-section-title">
                            Referenced Brands
                          </h4>
                          <div className="compliance-brand-tags">
                            {audit.referencedBrands.map((brand, i) => (
                              <span key={i} className="compliance-brand-tag">
                                {brand}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Status flags */}
                    <div className="compliance-flags">
                      {audit.tradeDressStripped && (
                        <span className="compliance-flag compliance-flag-stripped">
                          Trade Dress Stripped
                        </span>
                      )}
                      {audit.disclaimerInjected && (
                        <span className="compliance-flag compliance-flag-disclaimer">
                          Disclaimer Injected
                        </span>
                      )}
                      {isFixResult && audit.wasUpdated && (
                        <span className="compliance-flag compliance-flag-saved">
                          Saved to DB
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default ComplianceScanner;
